import React from 'react';
import SVGIcon from 'src/components/svg';
import { scssVariables as variables, events } from 'src/helpers';

// Render individual wave bars inside an SVG layout
function Wave({
  width = 200,
  height = 20,
  barWidth = 2,
  peaks = [], // Default to an empty array to prevent mapping errors before peaks are fetched
  progress = 0,
  waveColor = '#1da1f2',
  progressColor = '#00a0e0'
}) {
  return (
    <svg xmlns='http://w3.org' width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {peaks.map((peak, i) => {
        // Enforce safe boundary limits (between 10 and 100) based on your original formula
        let safePeak = peak;
        if (safePeak < 10) safePeak = 10;
        if (safePeak > 100) safePeak = 100;

        return (
          <rect
            key={i}
            width={barWidth}
            height={height}
            // Math adjustment: Space out bars perfectly (e.g., i * 4)
            transform={`translate(${i * 4} ${height - (safePeak / 100) * height})`}
            // Fills bar color conditionally based on the active tracking progress timestamp
            fill={progress > i / peaks.length ? progressColor : waveColor}
          />
        );
      })}
    </svg>
  );
}

export class AudioMedia extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      playing: false,
      time: 0,
      duration: '',
      generatedPeaks: null // Fallback storage for locally generated peak sequences
    };
  }

  componentDidMount() {
    events.on('playVoiceMessage', this.onPlayVoiceMessage);

    if (this.sound) {
      this.sound.addEventListener('ended', this.onPlayComplete);
      this.sound.addEventListener('error', this.onAudioError);
    }

    // Automatically trigger calculation if media peaks are absent or completely empty.
    const { media, enablePeaksExtraction = false } = this.props;
    if (media && (!media.peaks || media.peaks.length === 0)) {
      const source = media.file || media.uri;
      const isLocalFile = typeof Blob !== 'undefined' && source instanceof Blob;
      // Reading a local File/Blob (still uploading, not yet processed by the
      // server) only touches bytes already on the device via FileReader — no
      // network involved, so it's safe to always do this, everywhere
      // AudioMedia is used (e.g. previewing a voice message while it's still
      // uploading in a chat composer).
      // Reading a remote `media.uri` instead requires an actual network
      // fetch, which can fail for reasons outside our control (CORS, url not
      // ready yet, unsupported format, ...) — only worth that risk where a
      // caller has explicitly opted in (currently: FileBox's AudioPreview).
      if (source && (isLocalFile || enablePeaksExtraction)) {
        this.extractPeaksNative(source);
      }
    }
  }

  componentWillUnmount() {
    events.cancel('playVoiceMessage', this.onPlayVoiceMessage);
    if (this.interval) clearInterval(this.interval);

    if (this.sound) {
      this.sound.removeEventListener('ended', this.onPlayComplete);
      this.sound.removeEventListener('error', this.onAudioError);
    }
  }
  // Pure Web Audio API calculation tool to fetch peaks and duration without requiring UI libraries
  extractPeaksNative = async (audioUri) => {
    try {
      let arrayBuffer;

      // TRƯỜNG HỢP 1: audioUri là một đối tượng File hoặc Blob (Trước khi upload)
      if (audioUri instanceof Blob || audioUri instanceof File) {
        arrayBuffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read file buffer'));
          reader.readAsArrayBuffer(audioUri);
        });
      }
      // TRƯỜNG HỢP 2: audioUri là đường dẫn URL từ server (Sau khi upload)
      else if (typeof audioUri === 'string') {
        const response = await fetch(audioUri);
        if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);
        arrayBuffer = await response.arrayBuffer();
      } else {
        throw new Error('Invalid audio resource type');
      }

      // 2. Khởi tạo AudioContext và decode raw binary buffer data
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContextClass();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

      // --- Giữ nguyên logic xử lý peaks cũ của bạn ---
      const audioDuration = audioBuffer.duration;
      const channelData = audioBuffer.getChannelData(0);
      const targetSamples = 38;
      const blockSize = Math.floor(channelData.length / targetSamples);
      const rawExtractedPeaks = [];
      let maxPeakInAudio = 0;

      for (let i = 0; i < targetSamples; i++) {
        let start = i * blockSize;
        let max = 0;
        for (let j = 0; j < blockSize; j++) {
          const val = Math.abs(channelData[start + j]);
          if (val > max) max = val;
        }
        rawExtractedPeaks.push(max);
        if (max > maxPeakInAudio) maxPeakInAudio = max;
      }

      const normalizationFactor = maxPeakInAudio > 0 ? 100 / maxPeakInAudio : 1;
      const normalizedPeaks = rawExtractedPeaks.map((p) => Math.round(p * normalizationFactor));

      this.setState({
        generatedPeaks: normalizedPeaks,
        duration: audioDuration
      });

      audioCtx.close();
    } catch (error) {
      console.warn('Failed to extract audio peaks natively:', error);
    }
  };

  onAudioError = (err) => {
    console.warn('onAudioError', err);
  };

  playVoiceMessage = () => {
    events.emit('playVoiceMessage', this.msgId());
    if (this.props.onAudioClick) this.props.onAudioClick();
  };

  onPlayVoiceMessage = (playMsgId) => {
    let { playing } = this.state;
    let msgId = this.msgId();
    if (playing && playMsgId !== msgId) {
      this.stop();
    } else if (playMsgId === msgId) {
      playing ? this.pause() : this.play();
    }
  };

  play = () => {
    if (this.state.playing) return console.warn('already playing');
    this.setState({ playing: true });
    this.sound.play();
    if (this.interval) clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.state.playing) {
        this.setState({ time: this.sound.currentTime });
      }
    }, 250);
  };

  onPlayComplete = () => {
    if (this.sound) {
      this.setState({ playing: false, time: 0 });
      this.sound.currentTime = 0;
    }
  };

  pause = () => {
    this.sound.pause();
    if (this.state.playing) this.setState({ playing: false });
  };

  toggle = () => {
    if (this.state.playing) {
      this.pause();
    } else {
      this.play();
    }
  };

  stop = () => {
    this.sound.pause();
    this.sound.currentTime = 0;
    this.setState({ playing: false, time: 0 });
  };

  getMinSecs = (value) => {
    let result = '';
    if (value > 0) {
      let mins = Math.floor(value / 60);
      if (mins.toString().length === 1) mins = '0' + mins;
      let secs = Math.floor(value - mins * 60);
      if (secs.toString().length === 1) secs = '0' + secs;
      result = mins + ':' + secs;
    }
    return result;
  };

  msgId = () => {
    return this.props.itemId + '_' + this.props.media.counter;
  };

  render() {
    const {
      media,
      updated,
      classContainer = '',
      brChildren = null,
      onlyPreview = false
    } = this.props;
    let { uri } = media;

    // Prioritize parent-supplied peaks, fall back to locally extracted arrays if empty
    let peaks = media.peaks && media.peaks.length > 0 ? media.peaks : this.state.generatedPeaks;

    let { playing, time } = this.state;
    let duration = this.state.duration || media.duration;
    let progress = 0;
    if (duration > 0 && time > 0) progress = time / duration;
    let resultTime = this.getMinSecs(duration);
    if (time > 0) resultTime = this.getMinSecs(time) + ' / ' + resultTime;
    if (onlyPreview && time > 0) resultTime = this.getMinSecs(duration - time);

    return (
      <div
        className={'mc-audio ' + classContainer}
        style={onlyPreview ? { flexDirection: 'column' } : {}}
      >
        <audio ref={(ref) => (this.sound = ref)} src={uri} preload={'none'} loop={false} />
        <div
          className={'mc-pointer mc-no-style-btn mc-flex mc-flex-center'}
          onClick={() => (playing ? this.pause() : this.playVoiceMessage())}
        >
          <SVGIcon iconname={playing ? 'pause-icon' : 'play-icon'} width={40} reverseOp={true} />
        </div>
        <div className={'mc-content mc-flex-10-col'}>
          {/* Wave component renders safely as soon as the peak array has valid values */}
          {!onlyPreview && peaks && peaks.length > 0 && (
            <Wave
              width={150}
              height={20}
              peaks={peaks}
              progress={progress}
              updated={updated}
              waveColor={variables['colors-mc-audio-wave']}
              progressColor={variables['colors-mc-primary']}
            />
          )}
          <div className={'mc-flex'} style={{ alignItems: 'flex-end' }}>
            <div className={'mc-grow mc-audio-time'}>{resultTime}</div>
          </div>
        </div>
        {brChildren}
      </div>
    );
  }
}
